import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  customerCreateRequestSchema,
  type CustomerCreateRequest,
} from '@produzionejb/types';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@produzionejb/ui';
import { ApiError } from '../../lib/api-client';
import { useCreateCustomer, useCustomer, useUpdateCustomer } from '../../lib/customers-api';

const DEFAULT_VALUES: CustomerCreateRequest = {
  code: '',
  name: '',
  country: 'IT',
  vatNumber: null,
  address: null,
  email: null,
  phone: null,
  paymentTerms: null,
  active: true,
};

export function CustomerFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const customerQ = useCustomer(id);
  const createMut = useCreateCustomer();
  const updateMut = useUpdateCustomer(id ?? '');

  const form = useForm<CustomerCreateRequest>({
    resolver: zodResolver(customerCreateRequestSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (isEdit && customerQ.data) {
      form.reset({
        code: customerQ.data.code,
        name: customerQ.data.name,
        country: customerQ.data.country,
        vatNumber: customerQ.data.vatNumber,
        address: customerQ.data.address,
        email: customerQ.data.email,
        phone: customerQ.data.phone,
        paymentTerms: customerQ.data.paymentTerms,
        active: customerQ.data.active,
      });
    }
  }, [isEdit, customerQ.data, form]);

  async function onSubmit(values: CustomerCreateRequest) {
    // Normalizza: empty strings -> null
    const norm = <T extends string | null | undefined>(v: T): string | null =>
      typeof v === 'string' && v.trim() === '' ? null : (v ?? null);
    const payload: CustomerCreateRequest = {
      ...values,
      vatNumber: norm(values.vatNumber),
      email: norm(values.email),
      phone: norm(values.phone),
      paymentTerms: norm(values.paymentTerms),
      address: values.address && Object.values(values.address).some((v) => v && String(v).trim())
        ? values.address
        : null,
    };
    try {
      if (isEdit && id) {
        await updateMut.mutateAsync(payload);
      } else {
        await createMut.mutateAsync(payload);
      }
      navigate('/customers');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        form.setError('code', { message: t('customers.form.errorDuplicateCode') });
      } else {
        window.alert((err as Error).message);
      }
    }
  }

  const isSubmitting = createMut.isPending || updateMut.isPending;

  if (isEdit && customerQ.isLoading) {
    return <p className="text-sm text-slate-500">{t('common.loading')}</p>;
  }
  if (isEdit && customerQ.error) {
    return (
      <p className="text-sm text-red-600">
        {t('customers.form.errorLoad')}: {(customerQ.error as Error).message}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">
          {isEdit ? t('customers.form.titleEdit') : t('customers.form.titleNew')}
        </h2>
        <Button variant="secondary" onClick={() => navigate('/customers')}>
          {t('common.back')}
        </Button>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Card>
          <CardHeader>
            <CardTitle>{t('customers.form.sectionIdentity')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="code">{t('customers.fields.code')} *</Label>
              <Input id="code" autoComplete="off" {...form.register('code')} />
              {form.formState.errors.code && (
                <p className="text-xs text-red-600">{form.formState.errors.code.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="name">{t('customers.fields.name')} *</Label>
              <Input id="name" autoComplete="organization" {...form.register('name')} />
              {form.formState.errors.name && (
                <p className="text-xs text-red-600">{form.formState.errors.name.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="country">{t('customers.fields.country')} *</Label>
              <Input
                id="country"
                maxLength={2}
                placeholder="IT"
                autoComplete="country"
                {...form.register('country', {
                  setValueAs: (v) => (typeof v === 'string' ? v.toUpperCase() : v),
                })}
              />
              {form.formState.errors.country && (
                <p className="text-xs text-red-600">{form.formState.errors.country.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vatNumber">{t('customers.fields.vatNumber')}</Label>
              <Input
                id="vatNumber"
                placeholder="IT12345678901"
                {...form.register('vatNumber', {
                  setValueAs: (v) => (typeof v === 'string' ? v.toUpperCase() : v),
                })}
              />
              <p className="text-xs text-slate-500">{t('customers.form.vatHint')}</p>
              {form.formState.errors.vatNumber && (
                <p className="text-xs text-red-600">{form.formState.errors.vatNumber.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('customers.form.sectionContacts')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="email">{t('customers.fields.email')}</Label>
              <Input id="email" type="email" autoComplete="email" {...form.register('email')} />
              {form.formState.errors.email && (
                <p className="text-xs text-red-600">{form.formState.errors.email.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">{t('customers.fields.phone')}</Label>
              <Input id="phone" type="tel" autoComplete="tel" {...form.register('phone')} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="paymentTerms">{t('customers.fields.paymentTerms')}</Label>
              <Input
                id="paymentTerms"
                placeholder="Es. 30gg fine mese"
                {...form.register('paymentTerms')}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('customers.form.sectionAddress')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="addr-street">{t('customers.fields.addressStreet')}</Label>
              <Input id="addr-street" {...form.register('address.street')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="addr-city">{t('customers.fields.addressCity')}</Label>
              <Input id="addr-city" {...form.register('address.city')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="addr-postal">{t('customers.fields.addressPostalCode')}</Label>
              <Input id="addr-postal" {...form.register('address.postalCode')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="addr-province">{t('customers.fields.addressProvince')}</Label>
              <Input id="addr-province" {...form.register('address.province')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="addr-country">{t('customers.fields.addressCountry')}</Label>
              <Input
                id="addr-country"
                maxLength={2}
                {...form.register('address.country', {
                  setValueAs: (v) => (typeof v === 'string' ? v.toUpperCase() : v),
                })}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300"
                  {...form.register('active')}
                />
                {t('customers.fields.active')}
              </label>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            type="button"
            onClick={() => navigate('/customers')}
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isEdit ? t('common.save') : t('customers.form.createButton')}
          </Button>
        </div>
      </form>
    </div>
  );
}
